import type { DataSchema, Schema } from 'types';
import type { Table, VisibilityState } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';
import { memo, useState } from 'react';
import { Divider, ListItemText, Menu, MenuItem } from '@mui/material';
import { Restore, ViewColumn } from '@mui/icons-material';
import { Button } from 'components/mui/common/Button';
import { Checkbox } from 'components/mui/common/Checkbox';

const ColumnVisibilityControllerBase = <T extends keyof P, P extends DataSchema>({
  tableInstance,
  dataTestId,
  testIdPrefix,
  onAllColumnSizingReset,
  hasChangedColumnSizing = false,
}: {
  tableInstance: Table<Schema<T, P>>;
  dataTestId?: string;
  /**
   * This prop is needed to trigger a re-render when column visibility changes.
   * It is not used in the function body, but its presence in the props object ensures that
   * React.memo detects a change and updates the component.
   */
  columnVisibility?: VisibilityState;
  testIdPrefix?: string;
  onAllColumnSizingReset?: () => void;
  hasChangedColumnSizing?: boolean;
}) => {
  const resolvedDataTestId = dataTestId ?? (testIdPrefix ? `${testIdPrefix}-columns-button` : 'table-columns-button');
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleAllColumnSizingReset = () => {
    onAllColumnSizingReset?.();
    handleClose();
  };

  return (
    <>
      <Button
        data-testid={resolvedDataTestId}
        onClick={handleClick}
        variant="outlined"
        type="button"
        sx={{ paddingX: '22px' }}
        startIcon={<ViewColumn />}>
        {t('common.columns')}
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        sx={{ marginTop: '4px' }}
        slotProps={{ list: { sx: { marginY: '4px', maxHeight: '240px', overflow: 'auto', textOverflow: 'ellipsis' } } }}>
        {tableInstance.getAllLeafColumns().map((column) => {
          const header = column.columnDef.header;
          const label = typeof header === 'string' ? header : column.id;

          if (column.id === 'select' || column.id === 'actions' || column.id === 'collapse' || column.id === 'id' || column.id === 'hidden') {
            return null;
          }

          return (
            <MenuItem key={column.id} onClick={column.getToggleVisibilityHandler()}>
              <Checkbox checked={column.getIsVisible()} />
              <ListItemText primary={label} />
            </MenuItem>
          );
        })}
        {hasChangedColumnSizing && onAllColumnSizingReset && (
          <>
            <Divider />
            <MenuItem
              onClick={handleAllColumnSizingReset}
              data-testid={testIdPrefix ? `${testIdPrefix}-reset-column-widths` : 'table-reset-column-widths'}>
              <Restore fontSize="small" sx={{ marginRight: '8px' }} />
              <ListItemText primary={t('common.resetColumnWidths')} />
            </MenuItem>
          </>
        )}
      </Menu>
    </>
  );
};

export const ColumnVisibilityController = memo(ColumnVisibilityControllerBase) as unknown as typeof ColumnVisibilityControllerBase;
